import axios from 'axios'
import { auth } from '@/lib/firebase'
import { API_BASE } from '@/services/api'
import { toMillis } from '@shared/utils/timestamps'
import { billingRequest } from '@/utils/billingRequest'
import {
  IMMEDIATE_CANCELABLE_SUBSCRIPTION_STATUSES,
  PERIOD_END_CANCELABLE_SUBSCRIPTION_STATUSES,
  type UserSubscriptionStatus,
} from '@shared/schemas/users.schema'

export type { UserSubscriptionStatus } from '@shared/schemas/users.schema'

/**
 * Subscription data stored at users/{uid}/subscription in Firestore.
 *
 * Cancel status: 'canceled' is the canonical status for ended subscriptions.
 * Written by cancelSubscription.ts (user-initiated) and the
 * customer.subscription.deleted webhook (Stripe-confirmed).
 * 'inactive' is used for payment failures and account deletion.
 *
 * cancellationReason values (written alongside status on every deactivation):
 *   'user_requested'              - user canceled via cancelSubscription.ts
 *   'stripe_subscription_deleted' - Stripe confirmed via webhook
 *   'payment_failed'              - invoice.payment_failed webhook
 *   'account_deleted'             - user deleted their account
 */
export interface UserSubscription {
  status: UserSubscriptionStatus
  priceId?: string
  currentPeriodEnd?: string // ISO-8601 — backend converts Firestore Timestamp at the wire
  cancellationReason?: string
  canceledAt?: string
  stripeConfirmedCanceledAt?: string
}

const PERIOD_END_CANCELABLE_STATUSES: Set<UserSubscriptionStatus> = new Set(
  PERIOD_END_CANCELABLE_SUBSCRIPTION_STATUSES
)
const IMMEDIATELY_CANCELABLE_STATUSES: Set<UserSubscriptionStatus> = new Set(
  IMMEDIATE_CANCELABLE_SUBSCRIPTION_STATUSES
)

export const isSubscriptionCancelable = (status: UserSubscriptionStatus): boolean =>
  PERIOD_END_CANCELABLE_STATUSES.has(status) || IMMEDIATELY_CANCELABLE_STATUSES.has(status)

/**
 * Whether a cancellation of this subscription would take effect at period end.
 *
 * A stored `cancellationReason: 'user_requested'` says the user asked to cancel,
 * not that anything is still scheduled: the webhook clears that marker only on a
 * return to active or trialing, so it survives a slide into unpaid or paused,
 * where there is no paid period left to run out. Reading the marker alone there
 * hides the immediate-cancel control and shows nothing in its place.
 */
export const isPeriodEndSubscriptionCancellation = (status: UserSubscriptionStatus): boolean =>
  PERIOD_END_CANCELABLE_STATUSES.has(status)

export const isImmediateSubscriptionCancellation = (status: UserSubscriptionStatus): boolean =>
  IMMEDIATELY_CANCELABLE_STATUSES.has(status)

/** Null means no subscription; failed reads reject rather than suggesting checkout. */
export const requestUserSubscription = (uid: string, signal?: AbortSignal) =>
  billingRequest(
    uid,
    async (token, requestSignal) => {
      const { data } = await axios.get<{
        subscription: UserSubscription | null
        subscriptionRequired?: boolean
      }>(`${API_BASE}/userApi/v1/subscription`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: requestSignal,
      })
      // Older production Functions may not return this field until redeployed.
      // Treat anything except an explicit false as enforced to preserve access
      // for paying users without opening the gate for unsubscribed users.
      return { ...data, subscriptionRequired: data.subscriptionRequired === false ? false : true }
    },
    signal
  )

/**
 * Check if a subscription is active (can access dashboard)
 */
export const isSubscriptionActive = (subscription: UserSubscription | null): boolean => {
  if (!subscription) return false

  // Active and trialing subscriptions are entitled until their period ends.
  // (past_due is handled separately below — it must skip the period-end check.)
  if (subscription.status === 'active' || subscription.status === 'trialing') {
    // For subscriptions with currentPeriodEnd, check it's not expired.
    // `currentPeriodEnd` is an ISO-8601 string on the wire; toMillis parses it.
    if (subscription.currentPeriodEnd) {
      return toMillis(subscription.currentPeriodEnd) > Date.now()
    }
    return true
  }

  if (subscription.status === 'past_due') {
    // Payment failed after period end — currentPeriodEnd is legitimately in the past.
    // Stripe retries until subscription.deleted; access is preserved for the full
    // retry window, consistent with backend entitlements.service.ts behavior.
    return true
  }

  return false
}

/**
 * Cancel the user's subscription
 */
export const cancelSubscription = async (): Promise<void> => {
  const user = auth.currentUser
  if (!user) {
    throw new Error('No authenticated user')
  }

  const idToken = await user.getIdToken()
  const response = await fetch(`${API_BASE}/stripeApi/v1/cancelSubscription`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
  })

  if (!response.ok) {
    const errorData = await response.json()
    throw new Error(errorData.error || 'Failed to cancel subscription')
  }
}
